import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, UserCog } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import {
  adminApi,
  PAPEL_OPTIONS,
  PAPEL_LABEL,
  type TraccarUser,
} from '@/api/endpoints/admin';

export function OperatorsPage() {
  const qc = useQueryClient();
  const usersQ = useQuery({ queryKey: ['admin', 'traccar-users'], queryFn: () => adminApi.traccarUsers() });
  const profilesQ = useQuery({ queryKey: ['admin', 'user-profiles'], queryFn: () => adminApi.listUserProfiles() });

  const papelByUser = useMemo(() => {
    const m = new Map<number, string>();
    for (const p of profilesQ.data ?? []) m.set(p.user_id, p.papel);
    return m;
  }, [profilesQ.data]);

  const setMut = useMutation({
    mutationFn: async ({ userId, papel }: { userId: number; papel: string }) => {
      if (papel) await adminApi.setUserProfile(userId, papel);
      else await adminApi.removeUserProfile(userId);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'user-profiles'] }),
  });

  const users = (usersQ.data ?? []).slice().sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-4 sm:p-6">
      <div>
        <h1 className="flex items-center gap-2 text-xl font-bold">
          <UserCog className="h-5 w-5 text-primary" /> Operadores &amp; Permissões
        </h1>
        <p className="text-sm text-muted-foreground">
          Atribua papéis aos usuários do Traccar. Suporte Técnico acessa devices/chips/comandos;
          Financeiro acessa cobrança/faturas. Ambos são separados.
        </p>
      </div>

      <Card>
        <CardContent className="p-0">
          {usersQ.isLoading ? (
            <p className="p-6 text-center text-sm text-muted-foreground">Carregando…</p>
          ) : (
            <ul className="divide-y divide-border">
              {users.map((u) => (
                <UserRow
                  key={u.id}
                  user={u}
                  papel={papelByUser.get(u.id) ?? ''}
                  onChange={(papel) => setMut.mutate({ userId: u.id, papel })}
                  saving={setMut.isPending}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function UserRow({
  user,
  papel,
  onChange,
  saving,
}: {
  user: TraccarUser;
  papel: string;
  onChange: (papel: string) => void;
  saving: boolean;
}) {
  const isAdmin = user.administrator;
  const derived = isAdmin ? 'admin' : user.userLimit && user.userLimit !== 0 ? 'revenda' : 'cliente';
  const effective = papel || derived;

  return (
    <li className="flex flex-wrap items-center gap-3 px-4 py-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-semibold">{user.name}</span>
          <span
            className={cn(
              'rounded-full px-2 py-0.5 text-[10px] font-bold',
              effective === 'admin'
                ? 'bg-primary/10 text-primary'
                : effective.startsWith('operador')
                  ? 'bg-status-running/10 text-status-running'
                  : 'bg-muted text-muted-foreground',
            )}
          >
            {PAPEL_LABEL[effective] ?? effective}
          </span>
        </div>
        <p className="truncate text-xs text-muted-foreground">{user.email}</p>
      </div>
      {isAdmin ? (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-primary">
          <ShieldCheck className="h-3.5 w-3.5" /> Administrador
        </span>
      ) : (
        <select
          value={papel}
          disabled={saving}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
        >
          <option value="">Padrão ({PAPEL_LABEL[derived]})</option>
          {PAPEL_OPTIONS.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </select>
      )}
    </li>
  );
}
